import { specificationManualQuoteReasons } from '@misumi/shared'
import { useId, useState } from 'react'
import { DownOutlined, SettingOutlined } from '../../components/icons'
import {
  AS_MACHINED_FINISH,
  CONFIG_PROCESS_OPTIONS,
  toWireFinish,
  useManufacturingConfigCascade,
} from '../../components/manufacturing-config-cascade'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { formatMoney } from '../cart/cart-money'
import { useConfigurationEstimate } from './configuration-estimate'
import type { RfqLineSpecification } from './configuration-estimate'

export interface ConfigurePartsTarget {
  readonly id: string
  readonly name: string
}

export interface ConfigurePartsModalProps {
  readonly open: boolean
  /**
   * The lines being bulk-configured — the bulk toolbar's "Configure N"
   * action, and ONLY that entry point (UID-434/WS-D). The per-row Configure
   * button and the Requirements-cell pencil no longer mount this component
   * at all — they route to `RfqLineConfiguratorModal` (the shared
   * `PartConfiguratorShell`) instead, per the human ruling that the two
   * per-line entry points must not diverge from each other, only from this
   * bulk one.
   *
   * `targets.length` is USUALLY ≥2, but the toolbar can legitimately open
   * this with exactly one checkbox selected — "Bulk configure … 1 part" is a
   * real, intended state (UID-401), not something this component special-
   * cases. Design source: `dc.html:5731-5822`'s bulk modal (`hasBulkConfig`/
   * `bulkCfg`), opened only from the toolbar (`:4157`) via `onBulkConfigure`
   * → `openBulkConfig(rows)`.
   *
   * UID-401 previously had this component ALSO render a single-part
   * presentation for the (then-shared) per-row entry points at N=1; UID-434/
   * WS-D deletes that branch as dead code now that no caller can reach it
   * with N=1 — see `RfqLineConfiguratorModal`'s own doc comment for where
   * that presentation lives now. */
  readonly targets: readonly ConfigurePartsTarget[]
  readonly onClose: () => void
  /**
   * Called ONCE (never in a loop, ADR-0117 D9) with every target's line id,
   * the collected `RfqLineSpecification`, and an optional quantity override
   * (`null` = "keep each part's own quantity", the design's own placeholder
   * copy). The caller (`RfqReviewPage`) owns the actual
   * `POST /rfq/{rfqId}/lines/bulk-configure` call plus whatever draft-sync
   * it needs first — this modal has no `rfqId` and no opinion on how the
   * save reaches the server, only on what to save. Resolving `{ok:true}`
   * closes the modal; `{ok:false, message}` keeps it open and surfaces the
   * message inline (AC5's "Cancel closes without persisting" only applies to
   * Cancel/Escape/backdrop — a failed Save is not a silent no-op).
   */
  readonly onSave: (
    lineIds: readonly string[],
    specification: RfqLineSpecification,
    quantity: number | null,
  ) => Promise<{ readonly ok: true } | { readonly ok: false; readonly message: string }>
}

const QUANTITY_MIN = 1
const QUANTITY_MAX = 1_000_000

// ADR-0118 D1 (item 12) — the Inspection control is ONE `<select>` whose
// value space is {Standard, one catalog inspection, Custom}. `''` and the
// custom sentinel below can never collide with a real catalog inspection
// name (`packages/shared/src/manufacturing-catalog.ts`'s two strings are
// always non-empty, human-readable report names).
const STANDARD_INSPECTION_VALUE = ''
const CUSTOM_INSPECTION_VALUE = '__custom_inspection__'

function clampQuantity(text: string): number {
  return Math.min(QUANTITY_MAX, Math.max(QUANTITY_MIN, Math.round(Number(text)) || QUANTITY_MIN))
}

/**
 * UID-346 (ADR-0117) — the RFQ vertical's REAL "Bulk configure" modal,
 * replacing the UID-260/ADR-0085 stub that only ever collected three
 * free-text rows into `SpecSelection.attributes`. Source of truth:
 * `Fictiv Platform.dc.html` L5731-5822 (markup) / ~L8380-8400 (view-model) —
 * followed literally for geometry, copy and the three footer states.
 *
 * Mounted from ONLY the bulk toolbar's "Configure N" action (UID-434/WS-D —
 * see the `targets` prop doc for why the per-row/pencil entry points no
 * longer share this component). Everything below — the cascade fields, the
 * save path, the footer's manual-quote/estimate logic — is unchanged.
 * Conditionally MOUNTED by the caller (never kept
 * mounted with `open` merely toggled) so the cascade state genuinely resets
 * between openings (AC5) — the same posture `PartsBulkConfigureModal`'s own
 * caller (`PartsLibraryPage`) already takes, reused here rather than
 * reinvented.
 *
 * The process → material → finish cascade, and the material-gating for
 * Inspections/Certificates, are the SHARED `useManufacturingConfigCascade`
 * hook (`components/manufacturing-config-cascade.ts`) — EXTRACTED from
 * `PartsBulkConfigureModal.tsx` per ADR-0117 D9 so the two verticals' modals
 * cannot silently drift on the one thing that must never drift: which
 * materials/finishes/inspections/certificates a given process/material
 * combination actually offers. See that module's doc comment for what is
 * deliberately NOT shared (the Certificates control's geometry, the footer
 * copy, and the price-estimate source) and why.
 *
 * AC4 — the footer's live estimate is sourced from the REAL server pricing
 * seam (`useConfigurationEstimate` → `POST /pricing/configuration-estimate`),
 * NEVER a client-side computation. AC6 — Save is disabled until a material
 * is chosen (`canSave`, a LOCAL gate — see below), so it is impossible to
 * submit (and therefore impossible to mark a line configured with) an empty
 * specification; the real defect fix for that is in `review-domain.ts`'s
 * `needsConfigure`, covered by its own regression test.
 *
 * Reported defect fix (2026-09-10) — Finish is fully OPTIONAL to Save here:
 * `canSave` is `cascade.materialChosen` alone, deliberately weaker than the
 * shared `cascade.ready` (material AND finish, whenever the material offers
 * one). `cascade.ready` is left untouched — it still gates
 * `PartsBulkConfigureModal.tsx`'s Apply, an explicitly out-of-scope surface
 * that must keep requiring both — so only THIS modal's uses of the gate were
 * switched to the local `canSave`.
 */
export function ConfigurePartsModal({ open, targets, onClose, onSave }: ConfigurePartsModalProps) {
  const cascade = useManufacturingConfigCascade()
  const [busy, setBusy] = useState(false)
  const [saveError, setSaveError] = useState<string | undefined>(undefined)
  // ADR-0117 D4.1 — the design's "Custom" inspection option is a ROUTING
  // STATE (`customInspection: true`), NOT a catalog vocabulary member, so it
  // lives here as its own boolean rather than being pushed into
  // `cascade.inspections` (which stays strictly catalog-membership-bound).
  // Local state, not shared-cascade state: this control is specific to this
  // modal's design fidelity (the parts vertical's modal has no such option),
  // and resets for free because the caller unmounts this component between
  // openings (AC5), same as every other field here.
  const [customInspection, setCustomInspection] = useState(false)

  const processId = useId()
  const materialId = useId()
  const finishId = useId()
  const inspectionId = useId()
  const certId = useId()
  const certHeadingId = useId()
  // UID-584 — the pre-material Certificates helper text needs a stable id so
  // the disabled placeholder select's `aria-describedby` can point at it
  // (the reason it is disabled), not just its `aria-labelledby` name.
  const certHelpId = useId()
  const quantityId = useId()
  // Item 14 defect fix — the save-status footer needs a stable id so the
  // Save button's `aria-describedby` can point at it regardless of which
  // branch of the if/else below is currently rendered.
  const saveStatusId = useId()

  const count = targets.length
  const countLabel = `${count} part${count === 1 ? '' : 's'}`

  const specification: RfqLineSpecification = {
    process: cascade.process,
    material: cascade.material,
    finish: toWireFinish(cascade.finish),
    ...(cascade.inspections.length > 0 ? { inspections: [...cascade.inspections] } : {}),
    ...(cascade.certificates.length > 0 ? { certificates: [...cascade.certificates] } : {}),
    // Absent = false (no `default:` on the contract field, ADR-0117 D4.1) —
    // only sent when true, matching the pattern above.
    ...(customInspection ? { customInspection: true } : {}),
  }
  const quantityText = cascade.quantityText.trim()
  const quantity = quantityText ? clampQuantity(quantityText) : undefined

  // Reported defect fix (2026-09-10) — Finish is fully OPTIONAL for Save: the
  // local save gate is `materialChosen` alone, deliberately NOT
  // `cascade.ready` (which still requires a finish whenever the material
  // offers one). `cascade.ready` stays untouched here — it is shared with
  // `PartsBulkConfigureModal.tsx`, where Apply must still require both
  // material AND finish — so this modal reads `cascade.materialChosen`
  // directly at every gate instead. `finish: null` (the unselected state, via
  // `toWireFinish`) is a valid, priced specification for every material —
  // `manufacturing-catalog-lookup.ts`'s catalog-membership check only runs
  // when `finish !== null` — so firing the estimate on material alone is
  // correct, not merely permissive.
  const canSave = cascade.materialChosen

  // UID-604 / ADR-0177 §4e — THIS BULK SURFACE DELIBERATELY SENDS NO `partSeed`.
  //
  // `targets` is N parts sharing ONE specification and ONE estimate call, so
  // there is no single part whose seed this figure could honestly carry. The
  // rejected alternative was `targets[0].name`: it would be a confident lie
  // about the other N-1 parts, presented as their price. Passing `undefined`
  // instead makes the footer show the UN-SEEDED BASE — honestly "the base for
  // parts of this configuration, before per-part variation".
  //
  // The per-part figures are not lost, they are just not this surface's job:
  // the SAVE path (`POST /rfq/{rfqId}/lines/bulk-configure` →
  // `bulkConfigureLines` → `buildSpecSelection` per line) stamps each line with
  // ITS OWN seeded price, off the persisted `linePartIdentity` snapshot. That
  // split is already this operation's documented posture — the bulk-configure
  // operation is "NOT AN ESTIMATE ENDPOINT … this operation is the SAVE. It
  // recomputes the estimate server-side rather than trusting whatever the client
  // last displayed."
  const estimateState = useConfigurationEstimate(canSave, specification, quantity, undefined)

  // AC3 — "the label flips ... when custom inspection or any certificate is
  // selected" is a CLIENT-SIDE, IMMEDIATE rule (the design's own `hardRfq`,
  // dc.html ~L8382), not something that should visibly lag behind a
  // debounced network round trip. `manualQuoteForced` mirrors that: it does
  // NOT wait for `estimateState` to resolve.
  //
  // ADR-0117 D4.2 — the two specification-derived disjuncts come from the
  // SINGLE shared `specificationManualQuoteReasons` (also called by the D8
  // server adapter), not a second hand-kept copy. This is the fix for the
  // shipped false positive: the old predicate read `cascade.inspections.length
  // > 0` — ANY listed inspection, not specifically a CUSTOM one — which flipped
  // this to "Save & mark for RFQ" for an ordinary, auto-priceable, catalog
  // inspection like "Advanced Inspection Report". The shared function reads
  // only `customInspection` and `certificates` (plus a manual-quote process),
  // never `inspections`.
  //
  // The `estimateState.kind === 'manual_quote_required'` disjunct remains as
  // the safety net for triggers the specification alone does not determine
  // (e.g. a rate card with no entry for a material category) — the server
  // stays authoritative; this predicate never REPLACES it, only ORs on top.
  const manualQuoteReasons = specificationManualQuoteReasons(specification)
  const manualQuoteForced =
    manualQuoteReasons.length > 0 || estimateState.kind === 'manual_quote_required'

  const saveLabel = manualQuoteForced ? 'Save & mark for RFQ' : 'Save configuration'

  // UID-600 — the Certificates control is a single-select `<select>`
  // (dropdown), matching the design and the Process/Material/Finish/
  // Inspection chrome in this modal, replacing the former multi-select
  // popover-with-checkboxes. `cascade.certificates` itself stays the shared
  // cascade's MULTI-select array shape (ADR-0117 D9 — untouched here,
  // exactly as `cascade.inspections` was left untouched by the ADR-0118 D1
  // Inspection single-select above): `PartsBulkConfigureModal.tsx` still
  // relies on `cascade.certificates`/`toggleCertificate` for real multi-
  // select semantics, and the shared hook's own tests pin that shape.
  // This modal only ever puts 0 or 1 member into it, so its first (only)
  // entry IS the select's value — same narrowing technique
  // `handleInspectionChange` already uses for `cascade.inspections`.
  const certSelectValue = cascade.certificates[0] ?? ''

  /**
   * UID-600 — narrows the shared cascade's multi-select certificates array
   * to the single-select semantics this control needs, without touching
   * `manufacturing-config-cascade.ts` (out of ownership here, and shared
   * with the parts vertical's modal). Both branches go through
   * `toggleCertificate`, the only mutator the shared hook exposes for this
   * field; calling it twice in one handler is safe for the same reason
   * `handleInspectionChange` above is — it always sets state via a
   * functional updater (`toggleListItem(prev, id)`), so the two calls
   * compose in order rather than racing a stale snapshot.
   */
  function handleCertificateChange(value: string) {
    const current = cascade.certificates[0]
    if (value === (current ?? '')) return
    if (current) cascade.toggleCertificate(current)
    if (value !== '') cascade.toggleCertificate(value)
  }

  // ADR-0118 D1 (item 12) — derived select value for the single Inspection
  // control. `cascade.inspections` stays the shared cascade's array shape
  // (ADR-0117 D9 — not owned here), but this modal only ever puts 0 or 1
  // member into it, so its first (only) entry IS the select's value.
  const inspectionSelectValue = customInspection
    ? CUSTOM_INSPECTION_VALUE
    : (cascade.inspections[0] ?? STANDARD_INSPECTION_VALUE)

  /**
   * ADR-0118 D1 (item 12) — narrows the shared cascade's multi-select
   * inspections array to the single-select semantics this control needs,
   * without touching `manufacturing-config-cascade.ts` (out of ownership
   * here, and ADR-0117 D9 shares it with the parts vertical). Both branches
   * that touch `cascade.inspections` go through `toggleInspection`, which is
   * the only mutator the shared hook exposes; calling it twice in one
   * handler is safe because it always sets state via a functional updater
   * (`toggleListItem(prev, id)`), so the two calls compose in order rather
   * than racing against a stale snapshot.
   */
  function handleInspectionChange(value: string) {
    if (value === CUSTOM_INSPECTION_VALUE) {
      const current = cascade.inspections[0]
      if (current) cascade.toggleInspection(current)
      setCustomInspection(true)
      return
    }
    if (customInspection) setCustomInspection(false)
    const current = cascade.inspections[0]
    if (value === current) return
    if (current) cascade.toggleInspection(current)
    if (value !== STANDARD_INSPECTION_VALUE) cascade.toggleInspection(value)
  }

  // Item 14 defect fix — what the Save control's presentation must say when
  // the D5 gate (ADR-0117) is why it is disabled. Bound to the button via
  // `aria-describedby`/`title` below, and also rendered as the footer's
  // status text, so both surfaces stay a single source instead of the old
  // pair that could independently disagree.
  const saveBlockedReason = !canSave ? 'Choose a material to apply.' : undefined

  async function handleSave() {
    if (!canSave || busy) return
    setBusy(true)
    setSaveError(undefined)
    try {
      const result = await onSave(
        targets.map((target) => target.id),
        specification,
        quantity ?? null,
      )
      if (result.ok) {
        onClose()
      } else {
        setSaveError(result.message)
      }
    } catch {
      // Item 14 defect fix — `openapi-fetch` rethrows a rejected fetch (no
      // `onError` middleware, `auth-middleware.ts` only implements
      // `onRequest`/`onResponse`), so an offline/DNS/CORS/aborted-navigation
      // failure used to escape this function entirely: `saveError` stayed
      // `undefined`, `busy` flipped back via `finally`, and the modal just
      // sat there — "clicking does nothing" from the user's side. A generic,
      // retryable message is loud where silence was the bug.
      setSaveError('Something went wrong saving this configuration. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Bulk configure"
      maxWidth={560}
      /* UID-584 — shared footer treatment for the two bulk-configure modals
         (narrower primary button per the design, and the responsive
         behaviour the shared `.modal-footer` lacked between 360px and full
         width). Defined once as `.modal-bulk-configure` in ui.css; see that
         block for why it is scoped rather than applied to `.modal-footer`. */
      className="modal-bulk-configure"
      headerContent={
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 13, flex: 1, minWidth: 0 }}>
          <span className="rfq-bulk-configure-header-icon" aria-hidden="true">
            <SettingOutlined />
          </span>
          <div className="rfq-bulk-configure-header-text">
            <h2 className="modal-title">Bulk configure</h2>
            <p className="rfq-bulk-configure-description">
              Applying one configuration to <b>{countLabel}</b>. Every selected part will be updated
              and saved when you save.
            </p>
          </div>
        </div>
      }
      footer={
        <>
          {/* Item 14 defect fix — an `<output>` (implicit `status` live
           * region, same house pattern as `RequestSummaryRail.tsx`'s amber
           * callout) so a state change here — e.g. ticking Custom inspection
           * before a material is chosen — actually gets announced, instead
           * of the plain, silent `<span>` this used to be. Carries a stable
           * id so the Save button's `aria-describedby` always resolves,
           * regardless of which branch below is rendering. */}
          <output id={saveStatusId} className="rfq-bulk-configure-footer-status">
            {saveError ? (
              <span role="alert">{saveError}</span>
            ) : !canSave && manualQuoteForced ? (
              // Item 14 defect fix — the previous if/else ordering let
              // `!cascade.ready` win outright, so a buyer could see the Save
              // button read "Save & mark for RFQ" (manualQuoteForced, driven
              // by ticking Custom inspection — deliberately ungated per
              // ADR-0117 D4.1) while the footer silently swapped in the
              // generic "not ready" sentence — contradicting the label
              // instead of explaining it (ADR-0117 D4.2 §2: "the footer
              // sentence explains the label; sourcing them differently
              // guarantees the pair contradicts itself"). One merged
              // sentence carries BOTH facts, and this branch still renders
              // exclusively — never alongside the plain `!canSave` branch
              // below (ADR-0117 D4.2 §4). Readiness here is the LOCAL
              // `canSave` gate (material alone — reported defect fix,
              // 2026-09-10), not the shared `cascade.ready`.
              <span className="rfq-bulk-configure-footer-status-manual">
                Custom inspection / certificates selected — all parts will be submitted for a manual
                quote once you choose a material to apply.
              </span>
            ) : !canSave ? (
              <span>Choose a material to apply.</span>
            ) : manualQuoteForced ? (
              <span className="rfq-bulk-configure-footer-status-manual">
                Custom inspection / certificates selected — all parts will be submitted for a manual
                quote.
              </span>
            ) : estimateState.kind === 'loading' ? (
              <span>Estimating price…</span>
            ) : estimateState.kind === 'estimated' && estimateState.estimate.unitPrice ? (
              /* This figure is `indicativeUnitPrice` (`provenance: parametric`),
               * NEVER the binding, server-forced-null `unitPriceAmount`
               * (ADR-0085 D1, untouched).
               *
               * ⚠️ UID-584 (2026-09-10) — the always-visible caveat sentence
               * that used to render here ("Preview only — not a final price.
               * Actual pricing is confirmed once your RFQ is quoted.") was
               * REMOVED, to match the design mockup, which shows only
               * "Est. $X.XX / part · most parts auto-price".
               *
               * This is a DELIBERATE, INFORMED DEPARTURE from ADR-0117 D7,
               * not an oversight or a style tidy-up. D7 closes by requiring
               * that `RfqPreQuotePricingPreview`'s "always-visible plain-text
               * caveat is the accessibility pattern to follow (not hover-only
               * copy)", and the docs register it leans on (PR-PRC-01, RD-03)
               * requires an advisory figure be "marked as a preview (DM§14.3),
               * not a binding commitment". After this change the ONLY preview
               * marking left on this surface is the "Est." prefix.
               *
               * Authorized by om.dewangan@fictiv.com directly in conversation
               * on 2026-09-10, having been told explicitly that it contradicts
               * ADR-0117 D7. That is a human override, NOT a product or
               * compliance ruling — see the appended Amendment under D7 in
               * `docs/adr/0117-uid346-rfq-bulk-configure-typed-spec-and-
               * server-pricing-seam.md`, which records the override and flags
               * that a real ruling is still owed before this is permanent.
               *
               * UID-604 / ADR-0177 §4e — tail clause updated (product design)
               * from "most parts auto-price" to "each part's saved price may
               * vary": this modal shares ONE estimate across N parts with
               * different names/SKUs, and each part's ACTUAL saved price is
               * individually seeded (UID-604) so it can legitimately differ
               * from this shared figure. This is a minimal, in-place swap of
               * the tail clause only — it does NOT reinstate the two-line
               * caveat om.dewangan's override removed, so it stays compatible
               * with the one-line mockup constraint recorded above. Per
               * product design: confirm with the mockup owner before shipping,
               * since it changes copy that was the subject of that override.
               *
               * Do not re-add the caveat, and do not remove the remaining
               * "Est." marking, without reading that Amendment first. */
              <span>
                Est.{' '}
                <b>
                  {formatMoney(
                    estimateState.estimate.unitPrice.amount,
                    estimateState.estimate.unitPrice.currency,
                  )}
                </b>{' '}
                / part · each part&apos;s saved price may vary.
              </span>
            ) : (
              <span>We couldn't estimate this configuration. It can still be saved.</span>
            )}
          </output>
          {/* UID-584 — Cancel reads as the bordered/filled secondary button
              (design fidelity), not the plain-text link variant.

              `size="lg"` is the 40px step (`--control-height-lg`), matching
              the design's own footer geometry for this modal
              (`Fictiv Platform.dc.html:5821` — `height:40px;padding:0 16px`)
              and the 40px `.part-config-select` dropdowns directly above it.
              The default `md` step is 32px, which left both footer buttons
              visibly shorter than every other control in the same modal.
              Uses the EXISTING size scale rather than a new scoped
              height override, so the blast radius is these two buttons. */}
          <Button variant="secondary" size="lg" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          {/* Item 14 defect fix — the AFFORDANCE fix (below) is untouched:
           * `RequestSummaryRail`'s Submit already binds its disabled reason
           * via `title` (`:320-335`); this button lacked both that and any
           * programmatic link to the explanation, so a disabled "Save &
           * mark for RFQ" could read as dead with no way to discover why.
           * `aria-describedby` always points at the status output (whatever
           * it's currently saying); `title` carries the blocked reason
           * specifically, for a mouse user hovering the disabled control.
           *
           * Reported defect fix (2026-09-10) — the GATE itself is now the
           * local `canSave` (material alone), not `cascade.ready`
           * (material AND finish). `cascade.ready` stays exactly as it was
           * for `PartsBulkConfigureModal.tsx`, which still requires both. */}
          {/* UID-584 — `size="lg"` (40px) matches the design's Save geometry
              for this modal (`Fictiv Platform.dc.html:10304` `saveBase` —
              `height:40px;padding:0 20px;font-size:14px;font-weight:700`).
              See the Cancel button above for the full reasoning. */}
          <Button
            size="lg"
            onClick={handleSave}
            loading={busy}
            disabled={!canSave}
            aria-describedby={saveStatusId}
            title={saveBlockedReason}
          >
            {saveLabel}
          </Button>
        </>
      }
    >
      {targets.length > 0 && (
        <div className="rfq-bulk-configure-chips">
          {targets.map((target) => (
            <span key={target.id} className="rfq-bulk-configure-chip">
              {target.name}
            </span>
          ))}
        </div>
      )}

      <div className="part-config-fields">
        <div className="part-config-field">
          <label htmlFor={processId} className="part-config-field-label">
            Process
          </label>
          <div className="part-config-select-wrap">
            <select
              id={processId}
              className="part-config-select"
              value={cascade.process}
              onChange={(event) => cascade.setProcess(event.target.value as typeof cascade.process)}
            >
              {CONFIG_PROCESS_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
          </div>
        </div>

        <div className="part-config-field">
          <label htmlFor={materialId} className="part-config-field-label">
            Material
          </label>
          <div className="part-config-select-wrap">
            <select
              id={materialId}
              className="part-config-select"
              value={cascade.material}
              onChange={(event) => cascade.setMaterial(event.target.value)}
            >
              <option value="">Select material…</option>
              {cascade.materialsForProcess.map((option) => (
                <option key={option.id} value={option.name}>
                  {option.name}
                </option>
              ))}
            </select>
            <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
          </div>
        </div>

        <div className="part-config-field">
          <label htmlFor={finishId} className="part-config-field-label">
            Finish
          </label>
          <div className="part-config-select-wrap">
            {/* Reported defect fix (2026-09-10) — "As-machined / standard" is
             * now ALWAYS one of the options in a single, always-ENABLED
             * select, for every material, not just the 28 of 190 catalog
             * materials (e.g. "101 Copper", "ABS", "Delrin 150") that offer
             * NO secondary finish. Previously this control forked into a
             * disabled, hardcoded single-option select for those 28
             * materials and OMITTED the as-machined choice entirely for the
             * other 162 — so a buyer configuring an everyday material like
             * "6061 Aluminum" had no way to ask for "no secondary
             * operation." `cascade.finishOptions` is the single shared
             * source for this list ([] before a material is chosen,
             * otherwise the sentinel option prepended to the catalog's real
             * finishes for that process/material) — no second, hand-typed
             * option list here (ADR-0117 D1). The control still gates on
             * `cascade.materialChosen` (nothing to pick before then); it no
             * longer gates on `finishRequired` — that flag now only decides
             * whether the explanatory note below renders. `option.name`
             * stays the select's value (so `cascade.finish` holds the
             * sentinel string, `AS_MACHINED_FINISH`, when it's picked); the
             * wire value is derived at the `specification` object via
             * `toWireFinish`, which maps the sentinel (and '') back to
             * `null` — the server's own documented "only valid" answer for
             * "no secondary operation" (`manufacturing-catalog-lookup.ts`'s
             * `finishesFor` doc comment).
             *
             * Reported defect fix (2026-09-10) — "As-machined / standard" is
             * now the Finish control's DEFAULT initial choice once a
             * material is chosen, rather than the "Select finish…"
             * placeholder. The default is deliberately PRESENTATIONAL: the
             * `value` below falls back to `AS_MACHINED_FINISH` whenever
             * `cascade.finish` is still `''`, instead of a `setFinish` call
             * fired on material selection. `cascade.setMaterial` (in the
             * shared `manufacturing-config-cascade.ts`, out of ownership
             * here and shared with `PartsBulkConfigureModal.tsx`) resets
             * `finish` to `''`, and that hook's `finishChosen`/`ready` derive
             * from `finish.trim().length > 0` — writing the sentinel into
             * that shared state on material change would flip
             * `finishChosen`/`ready` to `true` there too, and the Parts
             * Library modal's 19 pinned tests assert Apply stays disabled
             * until a finish is EXPLICITLY chosen. A presentational fallback
             * changes nothing about `cascade.finish` itself, so only this
             * modal's rendered `<select>` sees the default — it cannot leak
             * into that shared gate. The wire value is unaffected either
             * way: `toWireFinish('')` and `toWireFinish(AS_MACHINED_FINISH)`
             * both resolve to `null`, so an untouched default submits
             * exactly what an untouched default submitted before this fix.
             * The "Select finish…" placeholder now renders ONLY while no
             * material is chosen (the disabled, empty-`finishOptions`
             * state) — once a material is chosen there is no "undecided"
             * state left to represent, since the sentinel itself IS the
             * answer for "no secondary operation"; offering both would let a
             * keyboard/AT user land on a placeholder that means nothing. */}
            <select
              id={finishId}
              className="part-config-select"
              value={cascade.materialChosen ? cascade.finish || AS_MACHINED_FINISH : cascade.finish}
              disabled={!cascade.materialChosen}
              aria-describedby={
                cascade.materialChosen && !cascade.finishRequired
                  ? `${finishId}-no-finish-note`
                  : undefined
              }
              onChange={(event) => cascade.setFinish(event.target.value)}
            >
              {!cascade.materialChosen && <option value="">Select finish…</option>}
              {cascade.finishOptions.map((option) => (
                <option key={option.id} value={option.name}>
                  {option.name}
                </option>
              ))}
            </select>
            <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
          </div>
          {!cascade.materialChosen && (
            <span className="caption-text">Select a material to see finish options.</span>
          )}
          {cascade.materialChosen && !cascade.finishRequired && (
            <span id={`${finishId}-no-finish-note`} className="caption-text">
              This material has no secondary finish — as-machined / standard only.
            </span>
          )}
        </div>

        {/* ADR-0118 D1 (item 12) — a single, always-visible, always-usable
         * `<select>`, sourced from the SAME `.part-config-*` chrome as
         * Process/Material/Finish above (ADR-0117 D9 — no `Select`
         * primitive forked in for one field). Replaces the checkbox
         * fieldset: the design's literal 4-option list (`standard`,
         * `advanced`, `fai`, `custom`) is NOT shipped — `advanced`/`fai` are
         * not catalog members and would 400 on submit
         * (`manufacturing-catalog-lookup.ts:320-324`), and the design's own
         * bulk modal prices/routes them as if they were a no-op, so there is
         * no coherent product intent to be faithful to. This ships instead:
         * `Standard inspection report` (omits `inspections`), the chosen
         * material's real catalog inspections VERBATIM (no design-label
         * alias table — that would be the hand-typed list ADR-0117 D1
         * forbids), and `Custom inspection` (routes to `customInspection`,
         * never a string in `inspections[]`). A `<select>` cannot be
         * labelled by a `<legend>`, so this is a real `<label htmlFor>`
         * rather than the fieldset/legend the checkbox version used.
         * Before a material is chosen this still offers Standard + Custom
         * (both material-independent), satisfying AC5 — visible and usable
         * with nothing selected yet. */}
        <div className="part-config-field">
          <label htmlFor={inspectionId} className="part-config-field-label">
            Inspection <span className="caption-text">(optional)</span>
          </label>
          <div className="part-config-select-wrap">
            <select
              id={inspectionId}
              className="part-config-select"
              value={inspectionSelectValue}
              onChange={(event) => handleInspectionChange(event.target.value)}
            >
              {/* UID-584 deliberately does NOT add the design's "(free)"
                  qualifier here, despite its own AC asking for it. ADR-0118
                  D1 constraint 2 forbids shipping "(free)" absent a product
                  confirmation: this app has no inspection price at compose
                  stage (ADR-0117 D8's estimator does not price inspections
                  at all), so "(free)" is an unbacked price assertion on a
                  control feeding a money-adjacent field. It was already
                  escalated once (ADR-0118 E1) and dropped at UID-359. Adding
                  it back is a cheap PM ruling — but a PM ruling, not a
                  fidelity detail. Do not re-add without one. */}
              <option value={STANDARD_INSPECTION_VALUE}>Standard inspection report</option>
              {cascade.availableInspections.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
              {/* ADR-0117 D4.1, carried into the single-select — this is the
               * design's "Custom" option (dc.html:10256-10258), a ROUTING
               * STATE (`customInspection: true`) rather than a catalog
               * lookup, so it is always offered independent of material.
               * The "requires a quote" consequence the checkbox label used
               * to carry now lives in the footer sentence
               * (`manualQuoteForced`), so the option label matches the
               * design's own copy exactly. */}
              <option value={CUSTOM_INSPECTION_VALUE}>Custom inspection</option>
            </select>
            <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
          </div>
        </div>

        <div className="part-config-field">
          <div id={certHeadingId} className="part-config-field-label">
            Certificates <span className="caption-text">(optional)</span>
          </div>
          {!cascade.materialChosen ? (
            <>
              <span id={certHelpId} className="caption-text">
                Select a material to see available certificates.
              </span>
              {/* UID-584 — a disabled placeholder control alongside (not
                  replacing) the helper text above, matching the
                  Process/Material/Finish `.part-config-select` chrome
                  everywhere else in this modal instead of leaving
                  Certificates as the only field with no control at all
                  before a material is chosen. This heading is a plain
                  `<div>` (not a `<legend>`, unlike the Parts modal), so the
                  disabled select's accessible name comes the same way, via
                  `aria-labelledby` pointing at that div's id;
                  `aria-describedby` points at the helper text so a screen
                  reader also gets the REASON it is unavailable, which
                  "disabled" alone does not convey. */}
              <div className="part-config-select-wrap">
                <select
                  className="part-config-select"
                  disabled
                  aria-labelledby={certHeadingId}
                  aria-describedby={certHelpId}
                >
                  <option value="">Select certificates...</option>
                </select>
                <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
              </div>
            </>
          ) : !cascade.certificatesAllowed ? (
            <span className="caption-text">Certificates aren't offered for this process.</span>
          ) : cascade.availableCertificates.length === 0 ? (
            <span className="caption-text">No certificates available for this material.</span>
          ) : (
            <div className="part-config-select-wrap">
              <select
                id={certId}
                className="part-config-select"
                value={certSelectValue}
                aria-labelledby={certHeadingId}
                onChange={(event) => handleCertificateChange(event.target.value)}
              >
                <option value="">None</option>
                {cascade.availableCertificates.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
            </div>
          )}
        </div>

        <div className="part-config-field part-config-field-quantity">
          <label htmlFor={quantityId} className="part-config-field-label">
            Quantity <span className="caption-text">(optional)</span>
          </label>
          <input
            id={quantityId}
            type="number"
            min={QUANTITY_MIN}
            max={QUANTITY_MAX}
            step={1}
            placeholder="Keep each part's qty"
            value={cascade.quantityText}
            onChange={(event) => cascade.setQuantityText(event.target.value)}
          />
        </div>
      </div>
    </Modal>
  )
}
